import React, { ReactElement } from 'react';
import AnswerData from '@ax2/common/model/answers-data/AnswerData';
import QuestionSchema from '@ax2/common/model/schemas/QuestionSchema';
import ListQuestionSchema from '@ax2/common/model/schemas/ListQuestionSchema';
import OptionsListAnswerData from '@ax2/common/model/answers-data/OptionsListAnswerData';
import QuestionType from '@ax2/common/model/QuestionType';
import { CheckboxListQuestion } from '../components/questions/CheckboxListQuestion';
import { CheckboxGridQuestion } from '../components/questions/CheckboxGridQuestion';
import GridQuestionSchema from '@ax2/common/model/schemas/GridQuestionSchema';
import OptionsGridAnswerData from '@ax2/common/model/answers-data/OptionsGridAnswerData';
import { DropdownMenuQuestion } from '../components/questions/DropdownMenuQuestion';
import SingleOptionAnswerData from '@ax2/common/model/answers-data/SingleOptionAnswerData';
import { OpenEndedTextQuestion } from '../components/questions/OpenEndedTextQuestion';
import TextAnswerData from '@ax2/common/model/answers-data/TextAnswerData';
import TextQuestionSchema from '@ax2/common/model/schemas/TextQuestionSchema';
import {
  getCheckboxGridQuestionData,
  getCheckboxListQuestionData,
  getDropdownMenuQuestionData,
  getOpenEndedNumericQuestionData,
  getOpenEndedTextQuestionData,
  getRadioButtonGridQuestionData,
  getRadiobuttonListQuestionData,
  getRankingListQuestionData,
  getRatingGridQuestionData,
  getTextboxGridNumericQuestionData,
  getTextboxListNumericQuestionData,
  getTextboxListQuestionData,
  getTextboxListSumQuestionData,
} from './questionsDataConverter';
import { RadioButtonGridQuestion } from '../components/questions/RadioButtonGridQuestion';
import { RadioButtonListQuestion } from '../components/questions/RadioButtonListQuestion';
import TextsGridAnswerData from '@ax2/common/model/answers-data/TextsGridAnswerData';
import TextsListAnswerData from '@ax2/common/model/answers-data/TextsListAnswerData';
import { TextboxGridNumericQuestion } from '../components/questions/TextboxGridNumericQuestion';
import { TextboxListNumericQuestion } from '../components/questions/TextboxListNumericQuestion';
import { TextboxListTextQuestion } from '../components/questions/TextboxListTextQuestion';
import { OpenEndedNumericQuestion } from '../components/questions/OpenEndedNumericQuestion';
import { RatingGridQuestion } from '../components/questions/RatingGridQuestion';
import { TextboxListSumQuestion } from '../components/questions/TextboxListSumQuestion';
import { RankingListQuestion } from '../components/questions/RankingListQuestion';
import RankingOptionAnswerData from '@ax2/common/model/answers-data/RankingOptionAnswerData';

export function getQuestion(
  qSchema: QuestionSchema,
  isDesktop: boolean,
  number: string,
  data: AnswerData | undefined,
  onCompletion: (data: AnswerData | undefined) => void
): ReactElement {
  switch (qSchema.questionType) {
    case QuestionType.CheckboxList:
      return React.createElement(
        CheckboxListQuestion,
        getCheckboxListQuestionData(
          qSchema as ListQuestionSchema,
          isDesktop,
          number,
          data ? (data as OptionsListAnswerData) : undefined,
          onCompletion
        )
      );
    case QuestionType.CheckboxGrid:
      return React.createElement(
        CheckboxGridQuestion,
        getCheckboxGridQuestionData(
          qSchema as GridQuestionSchema,
          isDesktop,
          number,
          data ? (data as OptionsGridAnswerData) : undefined,
          d => {
            onCompletion(d);
          }
        )
      );
    case QuestionType.DropdownMenu:
      return React.createElement(
        DropdownMenuQuestion,
        getDropdownMenuQuestionData(
          qSchema as ListQuestionSchema,
          isDesktop,
          number,
          data ? (data as SingleOptionAnswerData) : undefined,
          onCompletion
        )
      );
    case QuestionType.OpenEndedText:
      return React.createElement(
        OpenEndedTextQuestion,
        getOpenEndedTextQuestionData(
          qSchema as TextQuestionSchema,
          isDesktop,
          number,
          data ? (data as TextAnswerData) : undefined,
          onCompletion
        )
      );
    case QuestionType.OpenEndedNumeric:
      return React.createElement(
        OpenEndedNumericQuestion,
        getOpenEndedNumericQuestionData(
          qSchema as TextQuestionSchema,
          isDesktop,
          number,
          data ? (data as TextAnswerData) : undefined,
          onCompletion
        )
      );
    case QuestionType.RadioButtonGrid:
      return React.createElement(
        RadioButtonGridQuestion,
        getRadioButtonGridQuestionData(
          qSchema as GridQuestionSchema,
          isDesktop,
          number,
          data ? (data as OptionsGridAnswerData) : undefined,
          onCompletion
        )
      );
    case QuestionType.RadioButtonGridRow:
      return React.createElement(
        RadioButtonGridQuestion,
        getRadioButtonGridQuestionData(
          qSchema as GridQuestionSchema,
          isDesktop,
          number,
          data ? (data as OptionsGridAnswerData) : undefined,
          onCompletion
        )
      );
    //case QuestionType.YesNo:
    case QuestionType.RadioButtonList:
      return React.createElement(
        RadioButtonListQuestion,
        getRadiobuttonListQuestionData(
          qSchema as ListQuestionSchema,
          isDesktop,
          number,
          data ? (data as SingleOptionAnswerData) : undefined,
          onCompletion
        )
      );
    case QuestionType.TextboxGridNumeric:
      return React.createElement(
        TextboxGridNumericQuestion,
        getTextboxGridNumericQuestionData(
          qSchema as GridQuestionSchema,
          isDesktop,
          number,
          data ? (data as TextsGridAnswerData) : undefined,
          onCompletion
        )
      );
    case QuestionType.TextboxListNumeric:
      return React.createElement(
        TextboxListNumericQuestion,
        getTextboxListNumericQuestionData(
          qSchema as ListQuestionSchema,
          isDesktop,
          number,
          data ? (data as TextsListAnswerData).selected : undefined,
          d => onCompletion(new TextsListAnswerData(qSchema.id, d, null))
        )
      );
    case QuestionType.TextboxListText:
      return React.createElement(
        TextboxListTextQuestion,
        getTextboxListQuestionData(
          qSchema as ListQuestionSchema,
          isDesktop,
          number,
          data ? (data as TextsListAnswerData).selected : undefined,
          d => onCompletion(new TextsListAnswerData(qSchema.id, d, null))
        )
      );
    case QuestionType.ContinuousSumList:
      return React.createElement(
        TextboxListSumQuestion,
        getTextboxListSumQuestionData(
          qSchema as ListQuestionSchema,
          isDesktop,
          number,
          data ? (data as TextsListAnswerData).selected : undefined,
          d => onCompletion(new TextsListAnswerData(qSchema.id, d, null))
        )
      );
    case QuestionType.RatingList:
      return React.createElement(
        RatingGridQuestion,
        getRatingGridQuestionData(
          qSchema as GridQuestionSchema,
          isDesktop,
          number,
          data ? (data as OptionsGridAnswerData).selected : undefined,
          d => onCompletion(new OptionsGridAnswerData(qSchema.id, d, null))
        )
      );
    case QuestionType.RankingList:
      return React.createElement(
        RankingListQuestion,
        getRankingListQuestionData(
          qSchema as ListQuestionSchema,
          isDesktop,
          number,
          data ? (data as RankingOptionAnswerData).selected : undefined,
          d => onCompletion(new RankingOptionAnswerData(qSchema.id, d, null))
        )
      );
    default:
      throw Error(
        `Unknown QuestionType (${qSchema.questionType}) was used in question builder.`
      );
  }
}
